import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, CheckCircle, PenLine, ArrowLeft, ShieldCheck } from 'lucide-react';

// ── DocuSign inline signing components ───────────────────────────────────────

const FIELD_INPUT_STYLE = {
  initials:  'text-center font-bold tracking-widest',
  signature: 'font-serif italic',
  date:      'text-center text-gray-500',
  text:      '',
};

function SigningPage({ pdf, pageNum, fields, values, onChange }) {
  const wrapRef = useRef();
  const canvasRef = useRef();
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.floor(entry.contentRect.width);
      if (w > 0) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!pdf || !containerWidth) return;
    let cancelled = false;
    const render = async () => {
      const page = await pdf.getPage(pageNum);
      const native = page.getViewport({ scale: 1 });
      const dpr = window.devicePixelRatio || 1;
      const baseScale = containerWidth / native.width;
      const viewport = page.getViewport({ scale: baseScale * dpr });
      const canvas = canvasRef.current;
      if (!canvas || cancelled) return;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${viewport.width / dpr}px`;
      canvas.style.height = `${viewport.height / dpr}px`;
      await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
      if (!cancelled) setSize({ w: viewport.width / dpr, h: viewport.height / dpr });
    };
    render();
    return () => { cancelled = true; };
  }, [pdf, pageNum, containerWidth]);

  return (
    <div ref={wrapRef} className="w-full">
    <div className="relative shadow-lg mx-auto" style={{ display: 'inline-block', maxWidth: '100%' }}>
      <canvas ref={canvasRef} className="block" />
      {size.w > 0 && fields.map(field => {
        const isDate = field.type === 'date';
        const filled = values[field.id]?.trim();
        const fontSize = Math.max(9, Math.round(field.height * size.h * 0.52));
        return (
          <div
            key={field.id}
            className={`absolute flex items-center border-b-2 transition-colors ${
              filled
                ? 'border-blue-400 bg-blue-50/60'
                : 'border-dashed border-gray-400 bg-yellow-50/70 hover:bg-yellow-50'
            }`}
            style={{
              left: `${field.x * 100}%`,
              top: `${field.y * 100}%`,
              width: `${field.width * 100}%`,
              height: `${field.height * 100}%`,
            }}
          >
            <input
              type="text"
              className={`w-full h-full bg-transparent border-none outline-none px-1 text-gray-900 ${
                FIELD_INPUT_STYLE[field.type] || ''
              }`}
              style={{ fontSize }}
              placeholder={isDate ? '' : (field.label || field.type)}
              value={values[field.id] || ''}
              readOnly={isDate}
              tabIndex={isDate ? -1 : 0}
              onChange={e => !isDate && onChange(field.id, e.target.value)}
            />
          </div>
        );
      })}
    </div>
    </div>
  );
}

function InlineSigningView({ doc, submitting, onSubmit }) {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(true);

  const [values, setValues] = useState(() => {
    const today = new Date().toLocaleDateString();
    const init = {};
    for (const f of (doc.fields || [])) {
      if (f.type === 'date') init[f.id] = today;
    }
    return init;
  });

  const nonDateFields = (doc.fields || []).filter(f => f.type !== 'date');
  const filled = nonDateFields.filter(f => values[f.id]?.trim()).length;
  const progress = nonDateFields.length > 0 ? Math.round((filled / nonDateFields.length) * 100) : 100;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf');
        pdfjsLib.GlobalWorkerOptions.workerSrc =
          `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/file/`;
        const pdf = await pdfjsLib.getDocument(url).promise;
        if (!cancelled) { setPdfDoc(pdf); setPageCount(pdf.numPages); }
      } catch {
        if (!cancelled) toast.error('Failed to load PDF');
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [doc.id]);

  const handleSubmit = () => {
    const missing = nonDateFields.filter(f => !values[f.id]?.trim()).map(f => f.label || f.type);
    if (missing.length > 0) {
      toast.error(`Please fill in: ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? '...' : ''}`);
      return;
    }
    onSubmit(values);
  };

  return (
    <div className="space-y-4">
      {/* Progress */}
      <div className="flex items-center gap-3">
        <div className="flex-1 bg-muted rounded-full h-2">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <Badge variant="outline">{progress}% complete</Badge>
      </div>

      {pdfLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="flex flex-col items-center gap-8 bg-muted/30 rounded-lg p-4">
          {Array.from({ length: pageCount }, (_, i) => i + 1).map(pageNum => (
            <SigningPage
              key={pageNum}
              pdf={pdfDoc}
              pageNum={pageNum}
              fields={(doc.fields || []).filter(f => f.page === pageNum)}
              values={values}
              onChange={(id, val) => setValues(prev => ({ ...prev, [id]: val }))}
            />
          ))}
        </div>
      )}

      {/* Legal notice + submit */}
      <Card>
        <CardContent className="py-4 space-y-3">
          <div className="flex gap-3 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800 text-sm">
            <ShieldCheck className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="text-blue-800 dark:text-blue-200">
              By clicking <strong>Sign Document</strong>, you confirm that all entries above
              constitute a legally binding electronic signature. Your IP address and timestamp
              will be recorded.
            </div>
          </div>
          <Button className="w-full" size="lg" onClick={handleSubmit} disabled={submitting}>
            {submitting
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</>
              : <><PenLine className="h-4 w-4 mr-2" />Sign Document</>}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function DocumentSignPage() {
  const { documentId } = useParams();
  const [searchParams] = useSearchParams();
  const learnerId = searchParams.get('learner');
  const sectionId = searchParams.get('section_id');
  const campSectionId = searchParams.get('camp_section_id');
  const navigate = useNavigate();
  const { api, user } = useAuth();

  const [doc, setDoc] = useState(null);
  const [learner, setLearner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetchData();
  }, [documentId, learnerId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [docRes, learnersRes] = await Promise.all([
        api.get(`/documents/${documentId}/`),
        learnerId ? api.get(`/learners/${learnerId}/`) : Promise.resolve(null),
      ]);
      setDoc(docRes.data);
      if (learnersRes) setLearner(learnersRes.data);
    } catch {
      toast.error('Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  const handleInlineSubmit = async (fieldValues) => {
    setSubmitting(true);
    try {
      const data = new FormData();
      if (learnerId) data.append('learner_id', learnerId);
      if (sectionId) data.append('section_id', sectionId);
      if (campSectionId) data.append('camp_section_id', campSectionId);
      data.append('parent_email', user?.email || '');
      for (const [fieldId, value] of Object.entries(fieldValues)) {
        data.append(`initials_${fieldId}`, value);
      }
      await api.post(`/documents/${documentId}/sign/`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDone(true);
      toast.success('Document signed successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sign document');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!doc) {
    return <div className="text-center py-16 text-muted-foreground">Document not found.</div>;
  }

  if (done) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center space-y-4">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold">Document Signed!</h2>
        <p className="text-muted-foreground">
          You have successfully signed <strong>{doc.title}</strong>.
          A confirmation PDF has been generated for your records.
        </p>
        <div className="flex gap-3 justify-center pt-2">
          <Button variant="outline" onClick={() => navigate('/cart')}>Return to Cart</Button>
          <Button onClick={() => navigate('/parent/enrollments')}>My Enrollments</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{doc.title}</h1>
          {learner && (
            <p className="text-muted-foreground">
              Signing for: <strong>{learner.first_name} {learner.last_name}</strong>
            </p>
          )}
          {doc.description && <p className="text-sm text-muted-foreground mt-1">{doc.description}</p>}
        </div>
      </div>

      <InlineSigningView doc={doc} submitting={submitting} onSubmit={handleInlineSubmit} />
    </div>
  );
}
